/* eslint-disable no-unused-expressions */
/* eslint-disable react/jsx-wrap-multilines */
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Button,
  Checkbox,
  DatePicker,
  Fieldset,
  Form,
  Layout,
  Modal,
  PageWrap,
  Pagination,
  Spinner,
  Table,
  TableButton,
  TableButtonSorting,
} from '@vwfs-bronson/bronson-react';
import { Formik, FormikProps } from 'formik';
import moment from 'moment';
import { FormField } from '../../components/FormField/FormField';
import { MaskedTextFormField, masks } from '../../components/FormFields/MaskedTextFormField';
import { TextFormField } from '../../components/FormFields/TextFormField';
import { searchCustomerData } from '../../services/api/miles/miles-services';
import { isoDate, normalizeDate } from '../../services/common/format';
import { store } from '../../services/redux';
import { loadingActions, loadingSelectors } from '../../services/redux/features/loading.slice';
import { apiDateToUiDate } from '../FormPage/utils/formatDate';
import { getInitialValues } from './initial-values';
import { ProspectSearchPopupModalSchema } from './validationSchema';

interface Props {
  name?: any;
  label?: any;
  infoText?: string;
  readOnly?: any;
  isNumber?: any;
  setFieldValue?: any;
  onChange?: any;
  onBlur?: any;
  value?: any;
}

const DateOfBirth: FunctionComponent<Props> = ({ setFieldValue, name, label, infoText }) => (
  <FormField
    name={name}
    type="other"
    labelText={label}
    infoText={infoText}
    render={(fieldProps) => (
      <DatePicker
        {...fieldProps}
        name={name}
        datePickerClassName="c-input__disabled-validation"
        maxDate={moment().subtract(18, 'years').toDate()}
        dateFormat="d/m/Y"
        locale="fr"
        placeholder="j/m/a"
        mode="single"
        flatpickrOptions={{
          onClose: (e) => {
            const date = normalizeDate(e);
            try {
              if (date === 'Invalid date') {
                throw new Error('');
              }
              setFieldValue(name, normalizeDate(e));
            } catch (error) {
              setFieldValue(name, '');
            }
          },
        }}
        onChange={(e) => setFieldValue(name, e)}
        onBlur={(e) => setFieldValue(name, e)}
      />
    )}
  />
);

let prospectSearchData;

interface ProspectSearchPopupModalProps {
  visible: boolean;
  onCancel: any;
  onConfirm: any;
  cocontractor?: boolean;
}

export const ProspectSearchPopupModal: FunctionComponent<ProspectSearchPopupModalProps> = (props) => {
  const formikRef = useRef<FormikProps<Record<string, unknown>>>(null);
  const { t } = useTranslation();
  const { visible, onCancel, onConfirm, cocontractor } = props;

  const [sortLastName, setSortLastName] = useState('asc');
  const [sortFirstName, setSortFirstName] = useState('asc');
  const [sortCity, setSortCity] = useState('asc');
  const [customersDataList, setCustomerDataList] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<number>();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPageNumber, setTotalPageNumber] = useState(0);
  const [searchButtonClicked, setSearchButtonClicked] = useState(false);
  const isLoading = useSelector(loadingSelectors.selectLoading);

  const tableSortBy = (field, sortTyp, setFunc) => {
    switch (field) {
      case 'lastName':
        prospectSearchData = prospectSearchData.sort((a, b) => b.lastName.localeCompare(a.lastName));
        break;
      case 'firstName':
        prospectSearchData = prospectSearchData.sort((a, b) => b.firstName.localeCompare(a.firstName));
        break;
      case 'city':
        prospectSearchData = prospectSearchData.sort((a, b) => b.city.localeCompare(a.city));
        break;
      default:
        break;
    }
    if (sortTyp === 'desc') {
      prospectSearchData = prospectSearchData.sort().reverse();
      setFunc('asc');
    } else {
      setFunc('desc');
    }
  };

  const createSearchCustomerRequest = (prospectData) => {
    const { emailAdress: email, postalCode: zipCode, dateOfBirth, ...rest } = prospectData;
    return {
      email,
      zipCode,
      dateOfBirth: isoDate(dateOfBirth),
      ...rest,
    };
  };

  const onSubmit = async () => {
    prospectSearchData = customersDataList;
  };

  const onCustomerSearch = async (prospectData) => {
    store.dispatch(loadingActions.setLoading(true));
    setCustomerDataList([]);
    try {
      const mappedProspectData = createSearchCustomerRequest(prospectData);
      const searchProspectData = await searchCustomerData(mappedProspectData, currentPage);
      const prospectDataList = searchProspectData?.customers?.map((item) => {
        const formattedDateOfBirth = apiDateToUiDate(item?.customerData?.dateOfBirth);
        return cocontractor
          ? {
              genderCocontractor: item?.customerData?.gender,
              firstNameCocontractor: item?.customerData?.firstName,
              lastNameCocontractor: item?.customerData?.lastName,
              dateOfBirthCocontractor: formattedDateOfBirth,
              postalCode: item?.customerData?.addresses?.[0]?.zipCode,
              cityCocontractor: item?.customerData?.addresses?.[0]?.city,
              emailCocontractor: item?.customerData?.contactData?.emails?.[0]?.emailAddress,
              mobilePhoneCocontractor: item?.customerData?.contactData?.mobilePhoneNumber,
              telephonePhoneCocontractor: item?.customerData?.contactData?.telephoneNumber,
              milesCustomerId: item?.customerData?.externalIdentifiers?.[0]?.id,
            }
          : {
              gender: item?.customerData?.gender,
              firstName: item?.customerData?.firstName,
              lastName: item?.customerData?.lastName,
              dateOfBirth: formattedDateOfBirth,
              postalCode: item?.customerData?.addresses?.[0]?.zipCode,
              city: item?.customerData?.addresses?.[0]?.city,
              email: item?.customerData?.contactData?.emails?.[0]?.emailAddress,
              mobilePhone: item?.customerData?.contactData?.mobilePhoneNumber,
              telephonePhone: item?.customerData?.contactData?.telephoneNumber,
              milesCustomerId: item?.customerData?.externalIdentifiers?.[0]?.id,
            };
      });
      const totalPages = searchProspectData?.totalPages;
      setCustomerDataList(prospectDataList);
      setTotalPageNumber(totalPages);
      // eslint-disable-next-line no-empty
    } catch {
    } finally {
      store.dispatch(loadingActions.setLoading(false));
      setSearchButtonClicked(false);
    }
  };

  useEffect(() => {
    if (searchButtonClicked) {
      onCustomerSearch(formikRef?.current?.values);
      setSelectedRow(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchButtonClicked]);

  const onResetForm = () => {
    formikRef.current?.setValues({
      lastName: '',
      firstName: '',
      dateOfBirth: '',
      postalCode: '',
      city: '',
      email: '',
    });
    prospectSearchData = [];
  };

  const onNextClick = () => {
    if (currentPage < totalPageNumber) {
      setCurrentPage(currentPage + 1);
      setSearchButtonClicked(true);
    }
    setSelectedRow(undefined);
  };

  const onPrevClick = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setSearchButtonClicked(true);
    }
    setSelectedRow(undefined);
  };

  const shouldDisplayPage = (item, currentPage, totalPageNumber) => {
    const isFirstPage = item === 1;
    const isLastPage = item === totalPageNumber;
    const isCurrentPage = item === currentPage;
    const isNeighborOfCurrentPage = item === currentPage - 1 || item === currentPage + 1;
    const shouldShowFirstSet = currentPage < 4;
    const isInLastFourPages = currentPage > totalPageNumber - 3;

    return (
      (shouldShowFirstSet && item <= 4) ||
      isFirstPage ||
      isLastPage ||
      isCurrentPage ||
      isNeighborOfCurrentPage ||
      (isInLastFourPages && item > totalPageNumber - 4)
    );
  };

  const onItemClick = (item: number) => {
    if (item <= totalPageNumber) {
      setCurrentPage(item);
      setSearchButtonClicked(true);
    }
    setSelectedRow(undefined);
  };

  return (
    <Modal
      id="prospectSearchPopupModal"
      center
      stretch
      title={t('prospectSearchPopupModal:title')}
      buttonConfirmText={t('prospectSearchPopupModal:apply')}
      buttonCancelText={t('prospectSearchPopupModal:cancel')}
      shown={visible}
      confirmationDisabled={!selectedRow}
      buttonConfirmType="button"
      onClose={() => {
        onResetForm();
        onCancel();
        setSelectedRow(undefined);
        setCurrentPage(1);
        setCustomerDataList([]);
        setTotalPageNumber(0);
      }}
      onCancel={() => {
        onResetForm();
        onCancel();
        setSelectedRow(undefined);
        setCurrentPage(1);
        setCustomerDataList([]);
        setTotalPageNumber(0);
      }}
      onConfirm={() => {
        customersDataList && selectedRow && onConfirm(customersDataList[selectedRow - 1]);
        onResetForm();
        onCancel();
        setSelectedRow(undefined);
        setCurrentPage(1);
        setCustomerDataList([]);
        setTotalPageNumber(0);
      }}
      asPortal
      className="c-modal--large"
      style={{ overflow: 'hidden' }}
    >
      {/* Bronson advice: wrap content in a <div> with minHeight to avoid DatePicker clipping inside Modal */}
      <div style={{ minHeight: '430px' }}>
        <Formik
          enableReinitialize
          initialValues={getInitialValues}
          innerRef={formikRef}
          onSubmit={() => onSubmit()}
          validationSchema={ProspectSearchPopupModalSchema}
        >
          {(formProps) => {
            const { handleSubmit, setFieldValue, errors } = formProps;
            const handleSubmitOwn = (e) => {
              handleSubmit(e);
            };
            const isValid =
              formikRef?.current?.isValid && Object.keys(errors).length === 0 && formikRef?.current?.dirty;
            return (
              <Form onSubmit={handleSubmitOwn} className="u-pt-xxsmall">
                <PageWrap>
                  <Fieldset>
                    <Fieldset.Row>
                      <Layout className="u-ml">
                        <Layout className="u-3/4">
                          <Layout.Item default="1/3" s="1/1">
                            <TextFormField
                              fieldName="lastName"
                              labelTranslationKey="prospectSearchPopupModal:lastName"
                              optional
                            />
                          </Layout.Item>
                          <Layout.Item default="1/3" s="1/1">
                            <TextFormField
                              fieldName="firstName"
                              labelTranslationKey="prospectSearchPopupModal:firstName"
                              optional
                            />
                          </Layout.Item>
                          <Layout.Item default="1/3" s="1/1">
                            <DateOfBirth
                              name="dateOfBirth"
                              label={t('prospectSearchPopupModal:dateOfBirth')}
                              infoText={t('step1:dateInfoText:label')}
                              setFieldValue={setFieldValue}
                            />
                          </Layout.Item>
                          <Layout.Item default="1/3" s="1/1">
                            <MaskedTextFormField
                              fieldName="postalCode"
                              labelTranslationKey="prospectSearchPopupModal:postalCode"
                              mask={masks.zipCodeMask}
                              optional
                            />
                          </Layout.Item>
                          <Layout.Item default="1/3" s="1/1">
                            <TextFormField
                              fieldName="city"
                              labelTranslationKey="prospectSearchPopupModal:city"
                              optional
                            />
                          </Layout.Item>
                          <Layout.Item default="1/3" s="1/1">
                            <TextFormField
                              fieldName="email"
                              labelTranslationKey="prospectSearchPopupModal:email"
                              optional
                            />
                          </Layout.Item>
                        </Layout>
                        <Layout className="u-1/4" middle center>
                          <Layout.Item default="2/3" s="1/1" className="u-mt-xlarge u-ml@s">
                            <Button
                              key="b2"
                              icon="semantic-search"
                              ariaLabel="search"
                              onClick={() => {
                                setSearchButtonClicked(true);
                                setCurrentPage(1);
                              }}
                              disabled={!isValid}
                              secondary
                              round
                            />
                          </Layout.Item>
                        </Layout>
                      </Layout>
                    </Fieldset.Row>
                  </Fieldset>
                  <Fieldset className="u-pv-none">
                    <Table dataControls>
                      <React.Fragment key=".0">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th />
                            <Table.Th>
                              <TableButton
                                sort={TableButtonSorting.ASC}
                                type="button"
                                onClick={() => tableSortBy('lastName', sortLastName, setSortLastName)}
                              >
                                {t('prospectSearchPopupModal:lastName')}
                              </TableButton>
                            </Table.Th>
                            <Table.Th>
                              <TableButton
                                sort={TableButtonSorting.ASC}
                                onClick={() => tableSortBy('firstName', sortFirstName, setSortFirstName)}
                              >
                                {t('prospectSearchPopupModal:firstName')}
                              </TableButton>
                            </Table.Th>
                            <Table.Th>{t('prospectSearchPopupModal:dateOfBirth')}</Table.Th>
                            <Table.Th>{t('prospectSearchPopupModal:postalCode')}</Table.Th>
                            <Table.Th>
                              <TableButton
                                sort={TableButtonSorting.ASC}
                                onClick={() => tableSortBy('city', sortCity, setSortCity)}
                              >
                                {t('prospectSearchPopupModal:city')}
                              </TableButton>
                            </Table.Th>
                            <Table.Th>{t('prospectSearchPopupModal:email')}</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {customersDataList?.map((res, index) => (
                            <Table.Tr key={`table-row-${res.milesCustomerId}`} selected={selectedRow === index + 1}>
                              <Table.Td>
                                <Checkbox
                                  name="selectedRow"
                                  checked={selectedRow === index + 1}
                                  onChange={() => {
                                    selectedRow === index + 1 ? setSelectedRow(undefined) : setSelectedRow(index + 1);
                                  }}
                                />
                              </Table.Td>
                              <Table.Td>{res.lastName ? res.lastName : res.lastNameCocontractor}</Table.Td>
                              <Table.Td>{res.firstName ? res.firstName : res.firstNameCocontractor}</Table.Td>
                              <Table.Td>{res.dateOfBirth ? res.dateOfBirth : res.dateOfBirthCocontractor}</Table.Td>
                              <Table.Td>{res.postalCode ? res.postalCode : res.postalCodeCocontractor}</Table.Td>
                              <Table.Td>{res.city ? res.city : res.cityCocontractor}</Table.Td>
                              <Table.Td>{res.email ? res.email : res.emailCocontractor}</Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </React.Fragment>
                    </Table>
                  </Fieldset>
                </PageWrap>
              </Form>
            );
          }}
        </Formik>
        {totalPageNumber > 1 && (
          <div
            style={{
              display: 'inline-block',
            }}
          >
            <Fieldset className="u-pt-xxsmall">
              <Layout center>
                <Fieldset.Row>
                  <Layout.Item>
                    <Pagination
                      nextText=" > "
                      prevText=" < "
                      onNextClick={onNextClick}
                      onPrevClick={onPrevClick}
                      prevDisabled={currentPage === 1}
                      nextDisabled={totalPageNumber === currentPage}
                    >
                      {Array.from(Array(totalPageNumber + 1)?.keys())
                        ?.filter((item: number) => item !== 0 && shouldDisplayPage(item, currentPage, totalPageNumber))
                        .map((item: number, index, array: number[]) => {
                          const showThreeDots =
                            index < array.length - 1 && item !== totalPageNumber && array[index + 1] !== item + 1;
                          return (
                            <React.Fragment key={`${item}-pagination`}>
                              <Pagination.Item active={item === currentPage} onClick={() => onItemClick(item)}>
                                {item}
                              </Pagination.Item>
                              {showThreeDots && (
                                <Pagination.Item disabled key="ellipsis-pagination">
                                  ...
                                </Pagination.Item>
                              )}
                            </React.Fragment>
                          );
                        })}
                    </Pagination>
                  </Layout.Item>
                </Fieldset.Row>
              </Layout>
            </Fieldset>
          </div>
        )}
        {/* TODO Remove when Bronson fixes BRONSD-1042 */}
        {isLoading && <Spinner fullPage />}
      </div>
    </Modal>
  );
};
